import { useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Eyebrow, GoldDivider } from "../../components/brand";
import { RESOURCES } from "../../content/site";
import { api, formatApiError } from "../../lib/api";
import { track } from "../../lib/analytics";
import {
  ArrowLeft, ArrowUpRight, Clock, Download, FileText, HardDrive, Lock, Sparkles,
} from "lucide-react";

/**
 * Resource Detail Page — /resources/:slug
 *
 * Behavior:
 *  - If the resource is gated, we require email capture before revealing the
 *    download URL. Submitting posts to /public/guide-requests with resource_key
 *    so the CRM tags the lead with the requested resource.
 *  - Related resources are looked up by key (and filtered to those with PDFs).
 */
export default function ResourceDetail() {
  const { slug } = useParams();
  const r = useMemo(() => RESOURCES.find((x) => x.slug === slug), [slug]);
  const related = useMemo(() => {
    if (!r) return [];
    return (r.related_keys || [])
      .map((k) => RESOURCES.find((x) => x.key === k))
      .filter((x) => x && x.download_url);
  }, [r]);

  if (!r) return <Navigate to="/resources" replace />;
  // Hide unpublished resources (no PDF) — treat as 404 for the public.
  if (!r.download_url) return <Navigate to="/resources" replace />;

  return (
    <div data-testid={`resource-detail-${r.key}`}>
      {/* Hero */}
      <section className="bg-altura-espresso text-altura-ivory pt-20 pb-24 relative noise overflow-hidden">
        <div className="pointer-events-none absolute -right-32 -bottom-40 h-[520px] w-[520px] rounded-full border border-altura-gold/15 opacity-70" />
        <div className="container-editorial relative z-10">
          <Link to="/resources" className="inline-flex items-center gap-2 text-xs text-altura-ivory/70 hover:text-altura-gold transition" data-testid="resource-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Library
          </Link>
          <div className="mt-8 grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-5">
              <div className="rounded-lg border border-altura-gold/30 bg-white/5 p-3 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]">
                <img
                  src={r.image}
                  alt={r.title}
                  className="w-full rounded-md object-cover aspect-[3/4]"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <Eyebrow className="text-altura-gold">{r.category_label} · Altura Global Library</Eyebrow>
              <h1 className="mt-4 font-editorial text-4xl sm:text-5xl leading-[1.05] text-altura-ivory" data-testid="resource-title">
                {r.title}
              </h1>
              <GoldDivider className="mt-6" />
              <p className="mt-6 text-altura-ivory/75 leading-relaxed">{r.long_description || r.description}</p>

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
                <MetaTile icon={FileText} label="Format" value={r.type} />
                <MetaTile icon={Clock} label="Reading" value={r.reading_time} />
                {r.file_size_mb ? (
                  <MetaTile icon={HardDrive} label="Size" value={`${r.file_size_mb} MB`} />
                ) : null}
              </div>
              <div className="mt-8">
                <a href="#unlock" className="btn-gold" data-testid="resource-scroll-unlock">
                  {r.visibility === "gated" ? (
                    <>Unlock this resource <Lock className="h-4 w-4" /></>
                  ) : (
                    <>Download now <Download className="h-4 w-4" /></>
                  )}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unlock form */}
      <UnlockSection resource={r} />

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-altura-ivory py-24 border-t border-altura-espresso/5">
          <div className="container-editorial">
            <Eyebrow>Related from the Library</Eyebrow>
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {related.map((rel) => (
                <Link
                  key={rel.key}
                  to={`/resources/${rel.slug}`}
                  className="card-editorial overflow-hidden group block"
                  data-testid={`related-resource-${rel.key}`}
                >
                  <div className="aspect-[3/4] overflow-hidden bg-altura-beige/40">
                    <img src={rel.image} alt={rel.title} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <div className="eyebrow text-altura-gold-dark">{rel.category_label}</div>
                    <h3 className="mt-2 font-editorial text-xl text-altura-espresso leading-tight group-hover:text-altura-gold-dark transition-colors">{rel.title}</h3>
                    <div className="mt-4 inline-flex items-center gap-1 text-xs text-altura-espresso">
                      Open <ArrowUpRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function MetaTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md border border-altura-ivory/15 bg-white/5 px-4 py-3 backdrop-blur">
      <div className="flex items-center gap-2 text-altura-gold">
        <Icon className="h-3.5 w-3.5" />
        <span className="uppercase tracking-[0.16em] text-[10px]">{label}</span>
      </div>
      <div className="mt-1.5 font-editorial text-altura-ivory">{value}</div>
    </div>
  );
}

function UnlockSection({ resource: r }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", consent: true });
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [recos, setRecos] = useState([]);
  const [recoEngine, setRecoEngine] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/guide-requests", {
        first_name: f.first_name || "Investor",
        last_name: f.last_name,
        email: f.email,
        consent: true,
        resource_key: r.key,
        resource_title: r.title,
        source_page: `/resources/${r.slug}`,
      });
      const url = data.download_url || r.download_url;
      setDownloadUrl(url);
      toast.success("You're in — your download is ready.");
      track.resourceDownload(r.slug, r.title);
      // Best-effort analytics ping.
      try {
        await api.post(`/public/resources/${r.key}/increment-download`);
      } catch (_) { /* non-fatal */ }
      // AURI-powered "Recommended Next Resources"
      try {
        const publishedCatalog = RESOURCES
          .filter((x) => x.download_url)
          .map((x) => ({
            key: x.key, slug: x.slug, title: x.title,
            category: x.category, category_label: x.category_label,
            description: x.description, reading_time: x.reading_time,
            published: true,
          }));
        const recResp = await api.post("/public/resources/recommend", {
          current_resource_key: r.key,
          email: f.email,
          resources: publishedCatalog,
          max_results: 2,
        });
        setRecos(recResp.data?.recommendations || []);
        setRecoEngine(recResp.data?.engine || null);
      } catch (_) { /* non-fatal — widget just won't render */ }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const field = "w-full rounded-md border border-altura-espresso/15 bg-white px-3.5 py-2.5 text-sm text-altura-charcoal placeholder:text-altura-charcoal/40 focus:border-altura-gold focus:outline-none focus:ring-1 focus:ring-altura-gold/50";
  const label = "text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60";

  return (
    <section id="unlock" className="bg-altura-ivory py-24">
      <div className="container-editorial grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5">
          <Eyebrow>Unlock this resource</Eyebrow>
          <h2 className="mt-4 font-editorial text-3xl sm:text-4xl text-altura-espresso">
            {downloadUrl ? "Your resource is ready." : "Free — sent to your inbox."}
          </h2>
          <GoldDivider className="mt-6" />
          <p className="mt-6 text-altura-charcoal/70 leading-relaxed">
            Enter your name and email — we'll send you the PDF and add you to our advisor list for a personal follow-up if you'd like to explore any of the markets we serve.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="rounded-lg border border-altura-espresso/15 bg-white p-8 lg:p-10">
            {downloadUrl ? (
              <div className="text-center py-6" data-testid="resource-download-ready">
                <div className="eyebrow text-altura-gold-dark mx-auto">Complimentary</div>
                <h3 className="mt-3 font-editorial text-2xl text-altura-espresso">{r.title}</h3>
                <p className="mt-3 text-sm text-altura-charcoal/70">Click below to download your copy. We've also sent it to your inbox.</p>
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="btn-gold mt-6"
                  data-testid="resource-final-download"
                >
                  Download {r.type === "Flyer" ? "Flyer" : "PDF"} <Download className="h-4 w-4" />
                </a>

                {recos.length > 0 && (
                  <div className="mt-10 pt-8 border-t border-altura-espresso/10 text-left" data-testid="auri-recommended-next">
                    <div className="flex items-center gap-2 justify-center">
                      <Sparkles className="h-4 w-4 text-altura-gold-dark" />
                      <div className="eyebrow text-altura-gold-dark">
                        {recoEngine === "auri" ? "AURI recommends next" : "You might also enjoy"}
                      </div>
                    </div>
                    <div className="mt-5 grid gap-4 sm:grid-cols-2">
                      {recos.map((rec) => (
                        <Link key={rec.key} to={`/resources/${rec.slug}`}
                              className="group rounded-lg border border-altura-espresso/10 bg-altura-ivory p-5 text-left transition-all hover:border-altura-gold hover:shadow-[0_20px_50px_-15px_rgba(58,31,20,0.15)]"
                              data-testid={`auri-reco-${rec.key}`}>
                          <div className="eyebrow text-altura-gold-dark">{rec.category_label}</div>
                          <div className="mt-2 font-editorial text-lg text-altura-espresso leading-tight group-hover:text-altura-gold-dark transition-colors">
                            {rec.title}
                          </div>
                          {rec.why && (
                            <p className="mt-2 text-xs text-altura-charcoal/70 leading-relaxed italic">"{rec.why}"</p>
                          )}
                          <div className="mt-4 flex items-center justify-between text-[11px] text-altura-charcoal/50">
                            {rec.reading_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {rec.reading_time}</span>}
                            <span className="inline-flex items-center gap-1 text-altura-espresso group-hover:text-altura-gold-dark">
                              Continue reading <ArrowUpRight className="h-3 w-3" />
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="resource-unlock-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className={label}>First name *</label>
                    <input required className={field} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="resource-first-name" />
                  </div>
                  <div>
                    <label className={label}>Last name</label>
                    <input className={field} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="resource-last-name" />
                  </div>
                </div>
                <div>
                  <label className={label}>Email *</label>
                  <input type="email" required className={field} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="resource-email" />
                </div>
                <button type="submit" disabled={submitting} className="btn-gold w-full sm:w-auto disabled:opacity-60" data-testid="resource-submit">
                  {submitting ? "Sending…" : "Send Me the Resource"}
                </button>
                <p className="text-[11px] text-altura-charcoal/50 mt-2">
                  By requesting this resource you consent to be contacted by Altura Global. We treat your data with care.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
